import { InfiniteData } from '@reduxjs/toolkit/query/react'

import { APP } from 'src/Configs/App'
import { rtkApi } from 'src/Services/rtkApi'
import { Meta } from 'src/Types/common'
import {
  completedOrConfirmStatuses,
  monitoringStatuses,
  SoundStatus,
} from 'src/Pages/Creator/SoundsPage/hooks/useSoundStatus'
import { TikTokCampaignPricingModelEnum } from 'src/Types/campaigns'

import { Sound } from '../types'

export enum EventDismissReasonsEnum {
  PROFILE_BUSINESS = 'profile_business',
  PROFILE_DELETED = 'profile_deleted',
  PROFILE_PRIVATE = 'profile_private',
  VIDEO_DELETED = 'video_deleted',
  VIEWS_FAILED = 'views_failed',
}

export interface SubmittedSound extends Sound {
  status: SoundStatus
  id: number
  views: number
  reward: number
  trackTiktokId: string
  trackPlayUrl: string
  videoLink: string
  isMilestoneDaysPassed: boolean
  dismissReason: EventDismissReasonsEnum
  isPaymentReceived: boolean
  maxCreatorReward: number
  minCreatorReward: number
  minViews: number
  maxViews: number
  presenceMilestoneDate: string
  comment: string
  viewsMilestoneDate: string
  availabilityCaseOpenAt: string | null
  loweredViewsCaseOpenAt: string | null
  pricingModel?: TikTokCampaignPricingModelEnum
}

export interface SubmittedResponse {
  data: SubmittedSound[]
  meta: Meta
}

export enum SubmittedSoundsGroupEnum {
  ALL = 'all',
  MONITORING = 'monitoring',
  COMPLETED = 'completed',
}

const SUBMITTED_STATUSES = [
  SoundStatus.SUBMISSION_AWAITING,
  SoundStatus.SUBMISSION_REJECTED,
  SoundStatus.APPROVED_BY_ARTIST,
  SoundStatus.APPROVED_AUTOMATICALLY,
  SoundStatus.AWAITING_MODERATOR_REVIEW,
  SoundStatus.AWAITING_REVIEW,
  SoundStatus.APPROVED_BY_ADMIN,
  SoundStatus.DECLINED,
  SoundStatus.VIDEO_DISMISSED,
]

const submittedSoundsGroupFilters: Record<
  SubmittedSoundsGroupEnum,
  { statuses: SoundStatus[]; isPaymentReceived?: boolean }
> = {
  [SubmittedSoundsGroupEnum.ALL]: { statuses: SUBMITTED_STATUSES },
  [SubmittedSoundsGroupEnum.MONITORING]: {
    statuses: monitoringStatuses,
    isPaymentReceived: false,
  },
  [SubmittedSoundsGroupEnum.COMPLETED]: {
    statuses: completedOrConfirmStatuses,
    isPaymentReceived: true,
  },
}

const SUBMITTED_SOUNDS_PAGE_SIZE = 10
const FIRST_PAGE_NUMBER = 1

export const buildSubmittedSoundsUrl = (
  group: SubmittedSoundsGroupEnum,
  pageNumber: number,
): string => {
  const { statuses, isPaymentReceived } = submittedSoundsGroupFilters[group]
  const searchParams = new URLSearchParams({
    pageSize: String(SUBMITTED_SOUNDS_PAGE_SIZE),
    pageNumber: String(pageNumber),
  })

  statuses.forEach((status) => {
    searchParams.append('statuses', status)
  })

  if (typeof isPaymentReceived === 'boolean') {
    searchParams.append('isPaymentReceived', String(isPaymentReceived))
  }

  searchParams.append('sortBy', 'videoUploadDate.DESC')

  return `${APP.TIKTOK_SERVER}/creator/tiktok-campaign-events?${searchParams.toString()}`
}

interface UploadVideoData {
  campaignId: number
  paidAmount: number
  trackId: string
  presenceMilestoneDate: string
}

interface UploadVideoBody {
  videoTiktokUrl: string
  eventId: number
}

const creatorSubmittedSoundsApi = rtkApi.injectEndpoints({
  endpoints(build) {
    return {
      submittedSounds: build.infiniteQuery<
        SubmittedResponse,
        SubmittedSoundsGroupEnum,
        number
      >({
        infiniteQueryOptions: {
          initialPageParam: FIRST_PAGE_NUMBER,
          getNextPageParam: (lastPage, _allPages, lastPageParam) =>
            lastPage?.meta.hasNextPage ? lastPageParam + 1 : void 0,
        },
        query: ({ queryArg, pageParam }) => ({
          url: buildSubmittedSoundsUrl(queryArg, pageParam),
        }),
        providesTags: ['submittedSound'],
      }),
      submittedSoundById: build.query<SubmittedSound, number>({
        query: (soundId) => ({
          url: `${APP.TIKTOK_SERVER}/creator/tiktok-campaign-events/${soundId}`,
        }),
      }),
      submitVideo: build.mutation<UploadVideoData, UploadVideoBody>({
        query: ({ videoTiktokUrl, eventId }) => ({
          url: `${APP.TIKTOK_SERVER}/creator/tiktok/upload-video/${eventId}`,
          method: 'PUT',
          body: {
            videoTiktokUrl,
          },
        }),
        invalidatesTags: ['submittedSound', 'inProgressSound'],
      }),
    }
  },
})

export const getUniqueSubmittedSounds = (
  pages?: SubmittedResponse[],
): SubmittedSound[] => {
  const seenIds = new Set<number>()

  return (pages ?? [])
    .flatMap((page) => page.data)
    .filter((sound) => {
      if (seenIds.has(sound.id)) {
        return false
      }
      seenIds.add(sound.id)
      return true
    })
}

export const selectSubmittedSoundsCount = ({
  data,
}: {
  data?: InfiniteData<SubmittedResponse, number>
}): { submittedSoundsCount: number } => {
  const lastPage = data?.pages[data.pages.length - 1]

  return {
    submittedSoundsCount: lastPage?.meta.itemCount || 0,
  }
}

export const {
  useSubmittedSoundsInfiniteQuery,
  useSubmittedSoundByIdQuery,
  useSubmitVideoMutation,
} = creatorSubmittedSoundsApi
